export const lessonsData = [
  // ==================== ARRAY TRAVERSAL ====================
  {
    id: 'linear-search',
    title: 'Linear Search',
    module: 'Array Traversal',
    difficulty: 'Easy',
    estimatedTime: '10 min',
    concept: 'Sequential search through array elements',
    problemStatement: `Given an array of integers and a target value, return the index of the target in the array. If the target is not found, return -1.`,
    explanation: `Linear search examines each element of an array sequentially until the target is found or all elements have been checked. It's the simplest search algorithm with O(n) time complexity.`,
    realLifeAnalogy: `Like searching for a specific book on a shelf by checking each book one by one from left to right.`,
    constraints: ['1 <= array.length <= 1000', '-10^4 <= array[i], target <= 10^4'],
    examples: [
      { input: [2, 7, 11, 15], target: 11, output: 2 },
      { input: [5, 3, 8, 4, 2], target: 8, output: 2 },
      { input: [1, 2, 3, 4, 5], target: 6, output: -1 }
    ],
    javaCode: `public class Solution {
    public int linearSearch(int[] array, int target) {
        // Iterate through the array
        for (int i = 0; i < array.length; i++) {
            // Check if current element matches target
            if (array[i] == target) {
                return i; // Found at index i
            }
        }
        return -1; // Not found
    }
}`,
    visualizationType: 'array',
    requiresTarget: true
  },
  {
    id: 'product-except-self',
    title: 'Product of Array Except Self',
    module: 'Array Traversal',
    difficulty: 'Medium',
    estimatedTime: '15 min',
    concept: 'Build prefix and suffix products',
    problemStatement: `Given an integer array, return an array where each element is the product of all elements except itself, without using division.`,
    explanation: `This algorithm uses prefix and suffix products. First pass computes prefix products, second pass computes suffix products and combines them.`,
    realLifeAnalogy: `Like calculating team performance when each member is absent - you need to know the collective output of everyone else.`,
    constraints: ['2 <= array.length <= 1000', '-30 <= array[i] <= 30'],
    examples: [
      { input: [1, 2, 3, 4], target: null, output: [24, 12, 8, 6] },
      { input: [2, 3, 4, 5], target: null, output: [60, 40, 30, 24] },
      { input: [-1, 1, 0, -3, 3], target: null, output: [0, 0, 9, 0, 0] }
    ],
    javaCode: `public class Solution {
    public int[] productExceptSelf(int[] nums) {
        int n = nums.length;
        int[] result = new int[n];
        
        // Build prefix products
        result[0] = 1;
        for (int i = 1; i < n; i++) {
            result[i] = result[i - 1] * nums[i - 1];
        }
        
        // Build suffix products and multiply
        int suffix = 1;
        for (int i = n - 1; i >= 0; i--) {
            result[i] = result[i] * suffix;
            suffix *= nums[i];
        }
        
        return result;
    }
}`,
    visualizationType: 'array',
    requiresTarget: false
  },
  {
    id: 'first-missing-positive',
    title: 'First Missing Positive',
    module: 'Array Traversal',
    difficulty: 'Hard',
    estimatedTime: '20 min',
    concept: 'In-place array manipulation with index swapping',
    problemStatement: `Given an unsorted integer array, find the smallest missing positive integer. Must run in O(n) time and use O(1) space.`,
    explanation: `Use the array itself as a hash table by placing each positive number at its correct index (number k at index k-1). The first index without its correct number reveals the answer.`,
    realLifeAnalogy: `Like organizing numbered parking spots - each car with number N should park at spot N. The first empty spot reveals the missing number.`,
    constraints: ['1 <= array.length <= 1000', '-2^31 <= array[i] <= 2^31 - 1'],
    examples: [
      { input: [1, 2, 0], target: null, output: 3 },
      { input: [3, 4, -1, 1], target: null, output: 2 },
      { input: [7, 8, 9, 11, 12], target: null, output: 1 }
    ],
    javaCode: `public class Solution {
    public int firstMissingPositive(int[] nums) {
        int n = nums.length;
        
        // Place each number in its right position
        for (int i = 0; i < n; i++) {
            while (nums[i] > 0 && nums[i] <= n && 
                   nums[nums[i] - 1] != nums[i]) {
                // Swap nums[i] to its correct position
                int temp = nums[nums[i] - 1];
                nums[nums[i] - 1] = nums[i];
                nums[i] = temp;
            }
        }
        
        // Find first missing positive
        for (int i = 0; i < n; i++) {
            if (nums[i] != i + 1) {
                return i + 1;
            }
        }
        
        return n + 1;
    }
}`,
    visualizationType: 'array',
    requiresTarget: false
  },

  // ==================== TWO POINTER ====================
  {
    id: 'remove-duplicates',
    title: 'Remove Duplicates from Sorted Array',
    module: 'Two Pointer',
    difficulty: 'Easy',
    estimatedTime: '10 min',
    concept: 'Slow and fast pointer technique',
    problemStatement: `Given a sorted array, remove duplicates in-place and return the length of the unique elements. The relative order must be maintained.`,
    explanation: `Use two pointers: 'slow' tracks the position of unique elements, 'fast' scans through the array. When a new unique element is found, copy it to slow's position.`,
    realLifeAnalogy: `Like removing duplicate books from a sorted bookshelf - keep one copy and shift others to fill the gap.`,
    constraints: ['1 <= array.length <= 3 * 10^4', '-100 <= array[i] <= 100', 'Array is sorted'],
    examples: [
      { input: [1, 1, 2], target: null, output: 2 },
      { input: [0, 0, 1, 1, 1, 2, 2, 3, 3, 4], target: null, output: 5 },
      { input: [1, 2, 3, 4, 5], target: null, output: 5 }
    ],
    javaCode: `public class Solution {
    public int removeDuplicates(int[] nums) {
        if (nums.length == 0) return 0;
        
        int slow = 0;
        
        for (int fast = 1; fast < nums.length; fast++) {
            if (nums[fast] != nums[slow]) {
                slow++;
                nums[slow] = nums[fast];
            }
        }
        
        return slow + 1;
    }
}`,
    visualizationType: 'twoPointer',
    requiresTarget: false
  },
  {
    id: 'container-with-most-water',
    title: 'Container With Most Water',
    module: 'Two Pointer',
    difficulty: 'Medium',
    estimatedTime: '15 min',
    concept: 'Greedy two pointer approach',
    problemStatement: `Given an array of heights, find two lines that together with the x-axis form a container that holds the most water. Return the maximum area.`,
    explanation: `Start with pointers at both ends. Calculate area as min(height[left], height[right]) * width. Move the pointer with smaller height inward.`,
    realLifeAnalogy: `Like choosing two walls to hold water - you want maximum width and the limiting factor is always the shorter wall.`,
    constraints: ['2 <= array.length <= 10^5', '0 <= height[i] <= 10^4'],
    examples: [
      { input: [1, 8, 6, 2, 5, 4, 8, 3, 7], target: null, output: 49 },
      { input: [1, 1], target: null, output: 1 },
      { input: [4, 3, 2, 1, 4], target: null, output: 16 }
    ],
    javaCode: `public class Solution {
    public int maxArea(int[] height) {
        int left = 0;
        int right = height.length - 1;
        int maxArea = 0;
        
        while (left < right) {
            int width = right - left;
            int h = Math.min(height[left], height[right]);
            int area = width * h;
            maxArea = Math.max(maxArea, area);
            
            if (height[left] < height[right]) {
                left++;
            } else {
                right--;
            }
        }
        
        return maxArea;
    }
}`,
    visualizationType: 'twoPointer',
    requiresTarget: false
  },
  {
    id: 'trapping-rain-water',
    title: 'Trapping Rain Water',
    module: 'Two Pointer',
    difficulty: 'Hard',
    estimatedTime: '20 min',
    concept: 'Two pointer with left and right max tracking',
    problemStatement: `Given an elevation map where the width of each bar is 1, compute how much water can be trapped after raining.`,
    explanation: `Use two pointers and track the maximum height seen from left and right. Water trapped at any position is limited by min(left_max, right_max) minus current height.`,
    realLifeAnalogy: `Like calculating water collection between buildings - water level is determined by the shorter of the tallest buildings on either side.`,
    constraints: ['1 <= array.length <= 2 * 10^4', '0 <= height[i] <= 10^5'],
    examples: [
      { input: [0, 1, 0, 2, 1, 0, 1, 3, 2, 1, 2, 1], target: null, output: 6 },
      { input: [4, 2, 0, 3, 2, 5], target: null, output: 9 },
      { input: [3, 0, 2, 0, 4], target: null, output: 7 }
    ],
    javaCode: `public class Solution {
    public int trap(int[] height) {
        int left = 0, right = height.length - 1;
        int leftMax = 0, rightMax = 0;
        int water = 0;
        
        while (left < right) {
            if (height[left] < height[right]) {
                if (height[left] >= leftMax) {
                    leftMax = height[left];
                } else {
                    water += leftMax - height[left];
                }
                left++;
            } else {
                if (height[right] >= rightMax) {
                    rightMax = height[right];
                } else {
                    water += rightMax - height[right];
                }
                right--;
            }
        }
        
        return water;
    }
}`,
    visualizationType: 'twoPointer',
    requiresTarget: false
  },

  // ==================== SEARCHING ====================
  {
    id: 'binary-search',
    title: 'Binary Search',
    module: 'Searching',
    difficulty: 'Easy',
    estimatedTime: '10 min',
    concept: 'Divide and conquer with left/mid/right pointers',
    problemStatement: `Given a sorted array of integers and a target value, return the index of the target. If not found, return -1. Must run in O(log n) time.`,
    explanation: `Binary search repeatedly divides the sorted array in half. Compare target with middle element - if smaller, search left half; if larger, search right half. Eliminates half the elements each iteration.`,
    realLifeAnalogy: `Like finding a word in a dictionary by opening to the middle, then jumping left or right based on alphabetical order.`,
    constraints: ['1 <= array.length <= 10^4', '-10^4 <= array[i], target <= 10^4', 'Array is sorted'],
    examples: [
      { input: [-1, 0, 3, 5, 9, 12], target: 9, output: 4 },
      { input: [-1, 0, 3, 5, 9, 12], target: 2, output: -1 },
      { input: [1, 3, 5, 7, 9, 11, 13, 15], target: 7, output: 3 }
    ],
    javaCode: `public class Solution {
    public int binarySearch(int[] nums, int target) {
        int left = 0;
        int right = nums.length - 1;
        
        while (left <= right) {
            int mid = left + (right - left) / 2;
            
            if (nums[mid] == target) {
                return mid;
            } else if (nums[mid] < target) {
                left = mid + 1;
            } else {
                right = mid - 1;
            }
        }
        
        return -1;
    }
}`,
    visualizationType: 'search',
    requiresTarget: true
  },
  {
    id: 'search-rotated',
    title: 'Search in Rotated Sorted Array',
    module: 'Searching',
    difficulty: 'Medium',
    estimatedTime: '20 min',
    concept: 'Modified binary search on rotated arrays',
    problemStatement: `Given a rotated sorted array (originally sorted, then rotated at some pivot) and a target value, return its index or -1 if not found. Must run in O(log n) time.`,
    explanation: `At each step, one half of the array is always sorted. Determine which half is sorted, then check if target lies within that sorted range. If yes, search there; otherwise, search the other half.`,
    realLifeAnalogy: `Like finding a name in a rotated Rolodex - identify which section is still in alphabetical order, then decide which direction to look.`,
    constraints: ['1 <= array.length <= 5000', '-10^4 <= array[i], target <= 10^4', 'All values are unique'],
    examples: [
      { input: [4, 5, 6, 7, 0, 1, 2], target: 0, output: 4 },
      { input: [4, 5, 6, 7, 0, 1, 2], target: 3, output: -1 },
      { input: [6, 7, 8, 1, 2, 3, 4, 5], target: 3, output: 5 }
    ],
    javaCode: `public class Solution {
    public int search(int[] nums, int target) {
        int left = 0;
        int right = nums.length - 1;
        
        while (left <= right) {
            int mid = left + (right - left) / 2;
            
            if (nums[mid] == target) {
                return mid;
            }
            
            // Left half is sorted
            if (nums[left] <= nums[mid]) {
                if (target >= nums[left] && target < nums[mid]) {
                    right = mid - 1;
                } else {
                    left = mid + 1;
                }
            } else {
                // Right half is sorted
                if (target > nums[mid] && target <= nums[right]) {
                    left = mid + 1;
                } else {
                    right = mid - 1;
                }
            }
        }
        
        return -1;
    }
}`,
    visualizationType: 'search',
    requiresTarget: true
  },
  {
    id: 'find-peak-element',
    title: 'Find Peak Element',
    module: 'Searching',
    difficulty: 'Hard',
    estimatedTime: '20 min',
    concept: 'Binary search with slope analysis',
    problemStatement: `A peak element is greater than its neighbors. Find any peak element's index in O(log n) time. Assume nums[-1] = nums[n] = -infinity.`,
    explanation: `Use binary search based on slope: if nums[mid] < nums[mid+1], the peak lies to the right (going up). Otherwise, the peak is at mid or to the left. Guaranteed to converge to a peak.`,
    realLifeAnalogy: `Like climbing a mountain range - always move upward from your current position; you're guaranteed to reach a peak.`,
    constraints: ['1 <= array.length <= 1000', '-2^31 <= array[i] <= 2^31 - 1', 'Adjacent elements are distinct'],
    examples: [
      { input: [1, 2, 3, 1], target: null, output: 2 },
      { input: [1, 2, 1, 3, 5, 6, 4], target: null, output: 5 },
      { input: [1, 2, 3, 4, 5], target: null, output: 4 }
    ],
    javaCode: `public class Solution {
    public int findPeakElement(int[] nums) {
        int left = 0;
        int right = nums.length - 1;
        
        while (left < right) {
            int mid = left + (right - left) / 2;
            
            if (nums[mid] < nums[mid + 1]) {
                // Peak is on the right side
                left = mid + 1;
            } else {
                // Peak is at mid or on the left side
                right = mid;
            }
        }
        
        return left;
    }
}`,
    visualizationType: 'search',
    requiresTarget: false
  },

  // ==================== SORTING ====================
  {
    id: 'bubble-sort',
    title: 'Bubble Sort',
    module: 'Sorting',
    difficulty: 'Easy',
    estimatedTime: '15 min',
    concept: 'Compare and swap adjacent elements',
    problemStatement: `Sort an array in ascending order using Bubble Sort - repeatedly compare adjacent elements and swap if they are in wrong order.`,
    explanation: `In each pass, the largest unsorted element "bubbles up" to its correct position at the end. Optimize by tracking if any swap occurred; if not, the array is already sorted. O(n^2) worst case.`,
    realLifeAnalogy: `Like sorting cards in your hand by comparing adjacent pairs and swapping when needed - larger cards bubble to the right.`,
    constraints: ['1 <= array.length <= 100', '-1000 <= array[i] <= 1000'],
    examples: [
      { input: [64, 34, 25, 12, 22, 11, 90], target: null, output: [11, 12, 22, 25, 34, 64, 90] },
      { input: [5, 2, 8, 1, 9], target: null, output: [1, 2, 5, 8, 9] },
      { input: [3, 1, 4, 1, 5, 9, 2, 6], target: null, output: [1, 1, 2, 3, 4, 5, 6, 9] }
    ],
    javaCode: `public class Solution {
    public int[] bubbleSort(int[] nums) {
        int n = nums.length;
        
        for (int i = 0; i < n - 1; i++) {
            boolean swapped = false;
            
            for (int j = 0; j < n - i - 1; j++) {
                if (nums[j] > nums[j + 1]) {
                    // Swap adjacent elements
                    int temp = nums[j];
                    nums[j] = nums[j + 1];
                    nums[j + 1] = temp;
                    swapped = true;
                }
            }
            
            // Early exit if already sorted
            if (!swapped) break;
        }
        
        return nums;
    }
}`,
    visualizationType: 'sort',
    requiresTarget: false
  },
  {
    id: 'quick-sort',
    title: 'Quick Sort',
    module: 'Sorting',
    difficulty: 'Medium',
    estimatedTime: '20 min',
    concept: 'Divide and conquer with pivot partitioning',
    problemStatement: `Sort an array using Quick Sort - pick a pivot, partition the array so smaller elements are left of pivot and larger are right, then recursively sort partitions.`,
    explanation: `Choose the last element as pivot. Use a partition index that tracks the boundary of smaller elements. Scan through, swapping elements smaller than pivot to the left. Finally swap pivot into position. Average O(n log n).`,
    realLifeAnalogy: `Like organizing a lineup by height - pick a reference person, arrange shorter people on their left and taller on their right, then repeat for each group.`,
    constraints: ['1 <= array.length <= 100', '-1000 <= array[i] <= 1000'],
    examples: [
      { input: [10, 7, 8, 9, 1, 5], target: null, output: [1, 5, 7, 8, 9, 10] },
      { input: [3, 6, 8, 10, 1, 2, 1], target: null, output: [1, 1, 2, 3, 6, 8, 10] },
      { input: [5, 4, 3, 2, 1], target: null, output: [1, 2, 3, 4, 5] }
    ],
    javaCode: `public class Solution {
    public int[] quickSort(int[] nums) {
        quickSortHelper(nums, 0, nums.length - 1);
        return nums;
    }
    
    void quickSortHelper(int[] nums, int low, int high) {
        if (low < high) {
            int pivotIndex = partition(nums, low, high);
            quickSortHelper(nums, low, pivotIndex - 1);
            quickSortHelper(nums, pivotIndex + 1, high);
        }
    }
    
    int partition(int[] nums, int low, int high) {
        int pivot = nums[high];
        int i = low - 1;
        
        for (int j = low; j < high; j++) {
            if (nums[j] <= pivot) {
                i++;
                int temp = nums[i];
                nums[i] = nums[j];
                nums[j] = temp;
            }
        }
        
        int temp = nums[i + 1];
        nums[i + 1] = nums[high];
        nums[high] = temp;
        
        return i + 1;
    }
}`,
    visualizationType: 'sort',
    requiresTarget: false
  },
  {
    id: 'merge-sort',
    title: 'Merge Sort',
    module: 'Sorting',
    difficulty: 'Hard',
    estimatedTime: '25 min',
    concept: 'Divide, conquer, and merge sorted halves',
    problemStatement: `Sort an array using Merge Sort - recursively divide the array into halves, sort each half, then merge the sorted halves back together.`,
    explanation: `Merge Sort splits the array into single elements (which are inherently sorted), then merges them back in sorted order. The merge step combines two sorted arrays using two pointers. Guaranteed O(n log n) time.`,
    realLifeAnalogy: `Like combining two sorted stacks of graded papers into one sorted stack - compare the top papers and take the smaller one, repeat until done.`,
    constraints: ['1 <= array.length <= 100', '-1000 <= array[i] <= 1000'],
    examples: [
      { input: [38, 27, 43, 3, 9, 82, 10], target: null, output: [3, 9, 10, 27, 38, 43, 82] },
      { input: [5, 2, 4, 6, 1, 3], target: null, output: [1, 2, 3, 4, 5, 6] },
      { input: [12, 11, 13, 5, 6, 7], target: null, output: [5, 6, 7, 11, 12, 13] }
    ],
    javaCode: `public class Solution {
    public int[] mergeSort(int[] nums) {
        mergeSortHelper(nums, 0, nums.length - 1);
        return nums;
    }
    
    void mergeSortHelper(int[] nums, int left, int right) {
        if (left < right) {
            int mid = left + (right - left) / 2;
            mergeSortHelper(nums, left, mid);
            mergeSortHelper(nums, mid + 1, right);
            merge(nums, left, mid, right);
        }
    }
    
    void merge(int[] nums, int left, int mid, int right) {
        int[] temp = new int[right - left + 1];
        int i = left, j = mid + 1, k = 0;
        
        while (i <= mid && j <= right) {
            if (nums[i] <= nums[j]) {
                temp[k++] = nums[i++];
            } else {
                temp[k++] = nums[j++];
            }
        }
        
        while (i <= mid) temp[k++] = nums[i++];
        while (j <= right) temp[k++] = nums[j++];
        
        for (int m = 0; m < temp.length; m++) {
            nums[left + m] = temp[m];
        }
    }
}`,
    visualizationType: 'sort',
    requiresTarget: false
  },

  // ==================== RECURSION ====================
  {
    id: 'factorial',
    title: 'Factorial',
    module: 'Recursion',
    difficulty: 'Easy',
    estimatedTime: '10 min',
    concept: 'Classic recursion with base case',
    problemStatement: `Compute n! (factorial of n) using recursion. n! = n * (n-1) * (n-2) * ... * 1. By definition, 0! = 1.`,
    explanation: `Factorial recursively multiplies n by factorial(n-1) until it hits the base case (n=1 or n=0). Each recursive call adds a frame to the call stack, and return values propagate back as the stack unwinds.`,
    realLifeAnalogy: `Like a chain of workers where each one asks the next to compute part of the answer, then multiplies their own contribution before passing it back up.`,
    constraints: ['0 <= n <= 12 (to avoid overflow)'],
    examples: [
      { input: [5], target: null, output: 120 },
      { input: [6], target: null, output: 720 },
      { input: [1], target: null, output: 1 }
    ],
    javaCode: `public class Solution {
    public int factorial(int n) {
        // Base case
        if (n <= 1) {
            return 1;
        }
        // Recursive case
        return n * factorial(n - 1);
    }
}`,
    visualizationType: 'recursion',
    requiresTarget: false,
    singleNumberInput: true
  },
  {
    id: 'fibonacci',
    title: 'Fibonacci Number',
    module: 'Recursion',
    difficulty: 'Medium',
    estimatedTime: '15 min',
    concept: 'Recursion with multiple branches and memoization',
    problemStatement: `Return the nth Fibonacci number. Sequence: 0, 1, 1, 2, 3, 5, 8, 13, 21, ... where fib(n) = fib(n-1) + fib(n-2).`,
    explanation: `Naive recursion creates an exponential call tree. With memoization, we cache previously computed values, reducing time from O(2^n) to O(n). Each fib(n) waits for two subproblems before returning.`,
    realLifeAnalogy: `Like counting rabbit pairs where each pair produces a new pair every month - each month's count depends on the previous two months.`,
    constraints: ['0 <= n <= 20 (visualized) — memoization prevents exponential blowup'],
    examples: [
      { input: [6], target: null, output: 8 },
      { input: [7], target: null, output: 13 },
      { input: [10], target: null, output: 55 }
    ],
    javaCode: `public class Solution {
    public int fibonacci(int n) {
        // Base cases
        if (n <= 1) return n;
        // Recursive case
        return fibonacci(n - 1) + fibonacci(n - 2);
    }
}`,
    visualizationType: 'recursion',
    requiresTarget: false,
    singleNumberInput: true
  },
  {
    id: 'tower-of-hanoi',
    title: 'Tower of Hanoi',
    module: 'Recursion',
    difficulty: 'Hard',
    estimatedTime: '25 min',
    concept: 'Divide-and-conquer recursion with auxiliary state',
    problemStatement: `Move n disks from peg A to peg C using peg B as auxiliary. Rules: (1) Move one disk at a time (2) Never place larger disk on smaller one (3) Use all three pegs. Minimum moves: 2^n - 1.`,
    explanation: `To move n disks from source to target: recursively move top n-1 disks to auxiliary, move the largest disk to target, then recursively move n-1 disks from auxiliary to target. Beautiful demonstration of recursion.`,
    realLifeAnalogy: `Like a puzzle where a monk must move sacred rings between pillars. The trick: solve for smaller stacks first, then place the biggest ring, then repeat.`,
    constraints: ['1 <= n <= 6 (visualized) — total moves grow as 2^n - 1'],
    examples: [
      { input: [3], target: null, output: 7 },
      { input: [4], target: null, output: 15 },
      { input: [5], target: null, output: 31 }
    ],
    javaCode: `public class Solution {
    public int moveCount = 0;
    
    public void hanoi(int n, char source, char target, char aux) {
        if (n == 1) {
            System.out.println("Move disk 1 from " + source + " to " + target);
            moveCount++;
            return;
        }
        hanoi(n - 1, source, aux, target);
        System.out.println("Move disk " + n + " from " + source + " to " + target);
        moveCount++;
        hanoi(n - 1, aux, target, source);
    }
}`,
    visualizationType: 'recursion',
    requiresTarget: false,
    singleNumberInput: true
  },

  // ==================== BACKTRACKING ====================
  {
    id: 'subsets',
    title: 'Generate All Subsets',
    module: 'Backtracking',
    difficulty: 'Easy',
    estimatedTime: '15 min',
    concept: 'Include-exclude backtracking pattern',
    problemStatement: `Given an array of distinct integers, return all possible subsets (the power set). The solution set must not contain duplicate subsets. For n elements, there are 2^n subsets.`,
    explanation: `At each index, we decide: include or exclude. After exploring the "include" branch, we backtrack (remove the element) and explore the "exclude" branch. This generates all 2^n subsets systematically.`,
    realLifeAnalogy: `Like packing for a trip - for each item you own, decide "pack it" or "leave it". Every unique combination is a valid packing.`,
    constraints: ['1 <= array.length <= 6', 'All elements are distinct'],
    examples: [
      { input: [1, 2, 3], target: null, output: 8 },
      { input: [1, 2], target: null, output: 4 },
      { input: [1, 2, 3, 4], target: null, output: 16 }
    ],
    javaCode: `public class Solution {
    List<List<Integer>> result = new ArrayList<>();
    
    public List<List<Integer>> subsets(int[] nums) {
        backtrack(nums, 0, new ArrayList<>());
        return result;
    }
    
    void backtrack(int[] nums, int start, List<Integer> current) {
        result.add(new ArrayList<>(current));
        
        for (int i = start; i < nums.length; i++) {
            current.add(nums[i]);
            backtrack(nums, i + 1, current);
            current.remove(current.size() - 1);
        }
    }
}`,
    visualizationType: 'backtracking',
    requiresTarget: false
  },
  {
    id: 'permutations',
    title: 'Generate All Permutations',
    module: 'Backtracking',
    difficulty: 'Medium',
    estimatedTime: '20 min',
    concept: 'Swap-based backtracking',
    problemStatement: `Given an array of distinct integers, return all possible orderings (permutations). For n elements, there are n! permutations.`,
    explanation: `Fix one position at a time by swapping. For position i, try each remaining element by swapping it into position i, recurse, then swap back (backtrack). This generates all n! arrangements.`,
    realLifeAnalogy: `Like arranging books on a shelf - fix the first position, try each book there, then for each choice arrange the rest, repeating until every arrangement is tried.`,
    constraints: ['1 <= array.length <= 5', 'All elements are distinct'],
    examples: [
      { input: [1, 2, 3], target: null, output: 6 },
      { input: [1, 2], target: null, output: 2 },
      { input: [1, 2, 3, 4], target: null, output: 24 }
    ],
    javaCode: `public class Solution {
    List<List<Integer>> result = new ArrayList<>();
    
    public List<List<Integer>> permute(int[] nums) {
        backtrack(nums, 0);
        return result;
    }
    
    void backtrack(int[] nums, int start) {
        if (start == nums.length) {
            List<Integer> perm = new ArrayList<>();
            for (int n : nums) perm.add(n);
            result.add(perm);
            return;
        }
        
        for (int i = start; i < nums.length; i++) {
            swap(nums, start, i);
            backtrack(nums, start + 1);
            swap(nums, start, i);  // undo
        }
    }
    
    void swap(int[] a, int i, int j) {
        int t = a[i]; a[i] = a[j]; a[j] = t;
    }
}`,
    visualizationType: 'backtracking',
    requiresTarget: false
  },
  {
    id: 'n-queens',
    title: 'N-Queens',
    module: 'Backtracking',
    difficulty: 'Hard',
    estimatedTime: '30 min',
    concept: 'Constraint satisfaction with backtracking',
    problemStatement: `Place N queens on an NxN chessboard so that no two queens attack each other (no shared row, column, or diagonal). Find all distinct solutions.`,
    explanation: `Place queens row-by-row. For each row, try every column. Before placing, check if the position is under attack from any previously-placed queen. If safe, place and recurse; if not, try next column. If no column works, backtrack.`,
    realLifeAnalogy: `Like seating rival kingdoms at a chess-shaped table where no two rivals can see each other along any line - a careful arrangement puzzle.`,
    constraints: ['1 <= n <= 6 (visualized) — 8-queens has 92 solutions'],
    examples: [
      { input: [4], target: null, output: 2 },
      { input: [5], target: null, output: 10 },
      { input: [6], target: null, output: 4 }
    ],
    javaCode: `public class Solution {
    List<List<String>> result = new ArrayList<>();
    int[][] board;
    int n;
    
    public List<List<String>> solveNQueens(int n) {
        this.n = n;
        this.board = new int[n][n];
        solve(0);
        return result;
    }
    
    void solve(int row) {
        if (row == n) {
            result.add(buildBoard());
            return;
        }
        
        for (int col = 0; col < n; col++) {
            if (isSafe(row, col)) {
                board[row][col] = 1;
                solve(row + 1);
                board[row][col] = 0;  // backtrack
            }
        }
    }
    
    boolean isSafe(int row, int col) {
        // check column, diagonals
        return true;  // full logic in visualization
    }
}`,
    visualizationType: 'backtracking',
    requiresTarget: false,
    singleNumberInput: true
  },

  // ==================== DYNAMIC PROGRAMMING ====================
  {
    id: 'fibonacci-memo',
    title: 'Fibonacci (DP with Memoization)',
    module: 'Dynamic Programming',
    difficulty: 'Easy',
    estimatedTime: '10 min',
    concept: 'Bottom-up DP table (1D)',
    problemStatement: `Compute the nth Fibonacci number using dynamic programming. Fill a DP table bottom-up: dp[i] = dp[i-1] + dp[i-2]. This converts the O(2^n) recursive solution into O(n) time and O(n) space.`,
    explanation: `Instead of recomputing overlapping subproblems, we store each fib(i) in a table indexed by i. Each cell depends on the two cells before it, so we fill left-to-right and never repeat work.`,
    realLifeAnalogy: `Like keeping a notebook while calculating - instead of redoing math for every question, you look up your previous answers and build on them.`,
    constraints: ['0 <= n <= 30'],
    examples: [
      { input: [10], target: null, output: 55 },
      { input: [15], target: null, output: 610 },
      { input: [20], target: null, output: 6765 }
    ],
    javaCode: `public class Solution {
    public int fibonacci(int n) {
        if (n <= 1) return n;
        int[] dp = new int[n + 1];
        dp[0] = 0; dp[1] = 1;
        
        for (int i = 2; i <= n; i++) {
            dp[i] = dp[i - 1] + dp[i - 2];
        }
        
        return dp[n];
    }
}`,
    visualizationType: 'dp',
    requiresTarget: false,
    singleNumberInput: true
  },
  {
    id: 'lcs',
    title: 'Longest Common Subsequence',
    module: 'Dynamic Programming',
    difficulty: 'Medium',
    estimatedTime: '25 min',
    concept: '2D DP table with character matching',
    problemStatement: `Given two strings, find the length of their longest common subsequence (LCS). A subsequence keeps character order but doesn't need to be contiguous.`,
    explanation: `Build a 2D table where dp[i][j] = LCS of s1[0..i-1] and s2[0..j-1]. If characters match, dp[i][j] = dp[i-1][j-1] + 1. Otherwise, dp[i][j] = max(dp[i-1][j], dp[i][j-1]).`,
    realLifeAnalogy: `Like comparing two DNA sequences to find the longest shared genetic pattern - you don't need consecutive matches, just matches in order.`,
    constraints: ['1 <= |s1|, |s2| <= 20 (visualized)'],
    examples: [
      { input: 'ABCBDAB|BDCAB', target: null, output: 4 },
      { input: 'AGCAT|GAC', target: null, output: 2 },
      { input: 'ABCDGH|AEDFHR', target: null, output: 3 }
    ],
    javaCode: `public class Solution {
    public int lcs(String s1, String s2) {
        int m = s1.length(), n = s2.length();
        int[][] dp = new int[m + 1][n + 1];
        
        for (int i = 1; i <= m; i++) {
            for (int j = 1; j <= n; j++) {
                if (s1.charAt(i - 1) == s2.charAt(j - 1)) {
                    dp[i][j] = dp[i - 1][j - 1] + 1;
                } else {
                    dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
                }
            }
        }
        
        return dp[m][n];
    }
}`,
    visualizationType: 'dp',
    requiresTarget: false,
    customStringInput: true,
    inputPlaceholder: 'e.g., ABCBDAB|BDCAB',
    inputLabel: 'Two Strings (pipe-separated)'
  },
  {
    id: 'knapsack',
    title: '0/1 Knapsack',
    module: 'Dynamic Programming',
    difficulty: 'Hard',
    estimatedTime: '30 min',
    concept: '2D DP with weight capacity constraints',
    problemStatement: `Given items with weights and values, and a knapsack of fixed capacity, select items to maximize total value without exceeding the capacity. Each item can be taken at most once.`,
    explanation: `Build a 2D DP table where dp[i][c] = max value using first i items with capacity c. For each item: either skip it (dp[i-1][c]) or take it if it fits (dp[i-1][c-w] + v). Take the maximum.`,
    realLifeAnalogy: `Like packing a suitcase with a weight limit - for each item, decide whether including it gives more value than leaving it, considering what else you'll pack.`,
    constraints: ['1 <= items <= 6 (visualized)', '1 <= capacity <= 12'],
    examples: [
      { input: '1,3,4,5|1,4,5,7|7', target: null, output: 9 },
      { input: '2,3,4|3,4,5|5', target: null, output: 7 },
      { input: '1,2,3|10,15,40|6', target: null, output: 65 }
    ],
    javaCode: `public class Solution {
    public int knapsack(int[] weights, int[] values, int capacity) {
        int n = weights.length;
        int[][] dp = new int[n + 1][capacity + 1];
        
        for (int i = 1; i <= n; i++) {
            int w = weights[i - 1];
            int v = values[i - 1];
            
            for (int c = 0; c <= capacity; c++) {
                if (w <= c) {
                    dp[i][c] = Math.max(dp[i-1][c-w] + v, dp[i-1][c]);
                } else {
                    dp[i][c] = dp[i-1][c];
                }
            }
        }
        
        return dp[n][capacity];
    }
}`,
    visualizationType: 'dp',
    requiresTarget: false,
    customStringInput: true,
    inputPlaceholder: 'weights|values|capacity  e.g., 1,3,4,5|1,4,5,7|7',
    inputLabel: 'Weights | Values | Capacity'
  },

  // ==================== GRAPH ALGORITHMS ====================
  {
    id: 'bfs',
    title: 'Breadth-First Search',
    module: 'Graph',
    difficulty: 'Easy',
    estimatedTime: '15 min',
    concept: 'Level-by-level traversal using queue',
    problemStatement: `Traverse a graph in BFS order from a given start node. BFS explores all neighbors at the current depth before moving to nodes at the next depth level. Uses a queue (FIFO).`,
    explanation: `Add the start node to a queue. While queue is not empty: dequeue a node, mark it visited, and enqueue all its unvisited neighbors. This produces a level-order traversal.`,
    realLifeAnalogy: `Like ripples spreading from a stone dropped in a pond - each ripple reaches the next distance level before moving further outward.`,
    constraints: ['Pre-loaded 7-node graph', 'Start node: 0-6'],
    examples: [
      { input: [0], target: null, output: '0, 1, 2, 3, 4, 5, 6' },
      { input: [3], target: null, output: '3, 1, 6, 0, 4, 2, 5' },
      { input: [6], target: null, output: '6, 3, 4, 5, 1, 2, 0' }
    ],
    javaCode: `public class Solution {
    public List<Integer> bfs(Map<Integer,List<Integer>> adj, int start) {
        List<Integer> result = new ArrayList<>();
        Set<Integer> visited = new HashSet<>();
        Queue<Integer> queue = new LinkedList<>();
        queue.offer(start);
        
        while (!queue.isEmpty()) {
            int node = queue.poll();
            if (visited.contains(node)) continue;
            visited.add(node);
            result.add(node);
            
            for (int neighbor : adj.get(node)) {
                if (!visited.contains(neighbor)) {
                    queue.offer(neighbor);
                }
            }
        }
        
        return result;
    }
}`,
    visualizationType: 'graph',
    requiresTarget: false,
    singleNumberInput: true,
    inputLabel: 'Start Node (0-6)'
  },
  {
    id: 'dfs',
    title: 'Depth-First Search',
    module: 'Graph',
    difficulty: 'Medium',
    estimatedTime: '15 min',
    concept: 'Deep exploration using stack (or recursion)',
    problemStatement: `Traverse a graph in DFS order from a given start node. DFS explores as far as possible along each branch before backtracking. Uses a stack (LIFO) or recursion.`,
    explanation: `Push the start node onto a stack. While stack is not empty: pop a node, mark it visited, and push all its unvisited neighbors. This produces a deep-first traversal.`,
    realLifeAnalogy: `Like exploring a cave system - go as deep as possible into one tunnel before returning to try another branch.`,
    constraints: ['Pre-loaded 7-node graph', 'Start node: 0-6'],
    examples: [
      { input: [0], target: null, output: '0, 1, 3, 6, 4, 2, 5' },
      { input: [3], target: null, output: '3, 1, 0, 2, 4, 6, 5' },
      { input: [5], target: null, output: '5, 2, 0, 1, 3, 6, 4' }
    ],
    javaCode: `public class Solution {
    public List<Integer> dfs(Map<Integer,List<Integer>> adj, int start) {
        List<Integer> result = new ArrayList<>();
        Set<Integer> visited = new HashSet<>();
        Deque<Integer> stack = new ArrayDeque<>();
        stack.push(start);
        
        while (!stack.isEmpty()) {
            int node = stack.pop();
            if (visited.contains(node)) continue;
            visited.add(node);
            result.add(node);
            
            List<Integer> neighbors = adj.get(node);
            for (int i = neighbors.size() - 1; i >= 0; i--) {
                int neighbor = neighbors.get(i);
                if (!visited.contains(neighbor)) {
                    stack.push(neighbor);
                }
            }
        }
        
        return result;
    }
}`,
    visualizationType: 'graph',
    requiresTarget: false,
    singleNumberInput: true,
    inputLabel: 'Start Node (0-6)'
  },
  {
    id: 'dijkstra',
    title: "Dijkstra's Shortest Path",
    module: 'Graph',
    difficulty: 'Hard',
    estimatedTime: '30 min',
    concept: 'Greedy shortest-path with weighted edges',
    problemStatement: `Given a weighted graph and a source node, find the shortest distance from the source to every other node. All edge weights are non-negative.`,
    explanation: `Initialize distances to Infinity except source (0). Repeatedly pick the unvisited node with the smallest distance, mark it visited, and relax all its outgoing edges: if dist[u] + weight(u,v) < dist[v], update dist[v].`,
    realLifeAnalogy: `Like GPS navigation - starting from your location, keep expanding outward to the nearest unvisited point, always taking the shortest currently-known road.`,
    constraints: ['Pre-loaded 7-node weighted graph', 'Start node: 0-6', 'All weights positive'],
    examples: [
      { input: [0], target: null, output: '0:0, 1:2, 2:5, 3:6, 4:3, 5:7, 6:9' },
      { input: [3], target: null, output: '3:0, 1:4, 6:3, 0:6, 4:5, 5:7, 2:8' },
      { input: [6], target: null, output: '6:0, 3:3, 4:6, 5:4, 1:7, 2:6, 0:9' }
    ],
    javaCode: `public class Solution {
    public int[] dijkstra(int[][] graph, int start) {
        int n = graph.length;
        int[] dist = new int[n];
        Arrays.fill(dist, Integer.MAX_VALUE);
        dist[start] = 0;
        boolean[] visited = new boolean[n];
        
        for (int i = 0; i < n; i++) {
            // Pick unvisited node with min distance
            int u = -1;
            for (int v = 0; v < n; v++) {
                if (!visited[v] && (u == -1 || dist[v] < dist[u])) u = v;
            }
            if (dist[u] == Integer.MAX_VALUE) break;
            visited[u] = true;
            
            // Relax neighbors
            for (int v = 0; v < n; v++) {
                if (graph[u][v] > 0 && !visited[v]) {
                    int newDist = dist[u] + graph[u][v];
                    if (newDist < dist[v]) dist[v] = newDist;
                }
            }
        }
        
        return dist;
    }
}`,
    visualizationType: 'graph',
    requiresTarget: false,
    singleNumberInput: true,
    inputLabel: 'Start Node (0-6)'
  },

  // ==================== TREES ====================
  {
    id: 'inorder-traversal',
    title: 'Binary Tree Inorder Traversal',
    module: 'Trees',
    difficulty: 'Easy',
    estimatedTime: '15 min',
    concept: 'Recursive Left → Root → Right traversal',
    problemStatement: `Given a binary tree, return the inorder traversal of its node values. Inorder visits: Left subtree → Root → Right subtree.`,
    explanation: `Inorder traversal is defined recursively: for each node, first traverse the left subtree, then process the current node, then traverse the right subtree. For a BST, inorder produces values in sorted order.`,
    realLifeAnalogy: `Like reading a book table of contents in reading order — go through all left chapters, then this chapter, then all right chapters.`,
    constraints: ['Level-order array with -1 for null', '1 <= nodes <= 15 (visualized)'],
    examples: [
      { input: [1, 2, 3, 4, 5, -1, 6], target: null, output: '[4, 2, 5, 1, 3, 6]' },
      { input: [1, -1, 2, -1, -1, -1, 3], target: null, output: '[1, 2, 3]' },
      { input: [5, 3, 7, 2, 4, 6, 8], target: null, output: '[2, 3, 4, 5, 6, 7, 8]' }
    ],
    javaCode: `public class Solution {
    List<Integer> result = new ArrayList<>();
    
    public List<Integer> inorder(TreeNode root) {
        if (root == null) return result;
        
        inorder(root.left);   // Left
        result.add(root.val); // Root
        inorder(root.right);  // Right
        
        return result;
    }
}`,
    visualizationType: 'tree',
    requiresTarget: false,
    inputLabel: 'Tree (level-order, -1 for null)',
    inputPlaceholder: 'e.g., 1, 2, 3, 4, 5, -1, 6'
  },
  {
    id: 'level-order-traversal',
    title: 'Binary Tree Level Order Traversal',
    module: 'Trees',
    difficulty: 'Medium',
    estimatedTime: '20 min',
    concept: 'BFS on trees using a queue',
    problemStatement: `Given a binary tree, return the level-order traversal — visit all nodes level by level from left to right.`,
    explanation: `Use a queue (FIFO). Enqueue the root, then repeatedly dequeue a node, process it, and enqueue its children. This ensures nodes are processed in the order they appear from top to bottom, left to right.`,
    realLifeAnalogy: `Like reading a family tree generation by generation - first grandparents, then parents, then children.`,
    constraints: ['Level-order array with -1 for null', '1 <= nodes <= 15 (visualized)'],
    examples: [
      { input: [1, 2, 3, 4, 5, -1, 6], target: null, output: '[1, 2, 3, 4, 5, 6]' },
      { input: [3, 9, 20, -1, -1, 15, 7], target: null, output: '[3, 9, 20, 15, 7]' },
      { input: [1, 2, 3], target: null, output: '[1, 2, 3]' }
    ],
    javaCode: `public class Solution {
    public List<Integer> levelOrder(TreeNode root) {
        List<Integer> result = new ArrayList<>();
        if (root == null) return result;
        
        Queue<TreeNode> queue = new LinkedList<>();
        queue.offer(root);
        
        while (!queue.isEmpty()) {
            TreeNode node = queue.poll();
            result.add(node.val);
            
            if (node.left != null) queue.offer(node.left);
            if (node.right != null) queue.offer(node.right);
        }
        
        return result;
    }
}`,
    visualizationType: 'tree',
    requiresTarget: false,
    inputLabel: 'Tree (level-order, -1 for null)',
    inputPlaceholder: 'e.g., 3, 9, 20, -1, -1, 15, 7'
  },
  {
    id: 'lca',
    title: 'Lowest Common Ancestor',
    module: 'Trees',
    difficulty: 'Hard',
    estimatedTime: '25 min',
    concept: 'Recursive tree traversal with subtree info',
    problemStatement: `Given a binary tree and two node values p and q, find their Lowest Common Ancestor (LCA) - the deepest node that has both p and q as descendants (a node can be its own ancestor).`,
    explanation: `Recursively search left and right subtrees. If both return non-null, the current node is the LCA (p and q are on different sides). Otherwise, return whichever side found something. Base case: if node is null, or matches p or q, return it.`,
    realLifeAnalogy: `Like finding the closest common ancestor in a family tree - trace both people upward until their paths first meet.`,
    constraints: ['Level-order array with -1 for null', 'All values unique', 'p and q exist in the tree'],
    examples: [
      { input: '3,5,1,6,2,0,8,-1,-1,7,4|5|4', target: null, output: 5 },
      { input: '3,5,1,6,2,0,8,-1,-1,7,4|7|8', target: null, output: 3 },
      { input: '1,2,3,4,5,6,7|4|7', target: null, output: 1 }
    ],
    javaCode: `public class Solution {
    public TreeNode lca(TreeNode root, TreeNode p, TreeNode q) {
        // Base case
        if (root == null) return null;
        if (root == p || root == q) return root;
        
        // Search both subtrees
        TreeNode left = lca(root.left, p, q);
        TreeNode right = lca(root.right, p, q);
        
        // p and q on different sides — current is LCA
        if (left != null && right != null) return root;
        
        // Otherwise pass up whichever side found something
        return left != null ? left : right;
    }
}`,
    visualizationType: 'tree',
    requiresTarget: false,
    customStringInput: true,
    inputLabel: 'Tree | p | q',
    inputPlaceholder: 'e.g., 3,5,1,6,2,0,8,-1,-1,7,4|5|4'
  },

  // ==================== STRING ALGORITHMS ====================
  {
    id: 'naive-string-match',
    title: 'Naive String Matching',
    module: 'String Algorithms',
    difficulty: 'Easy',
    estimatedTime: '12 min',
    concept: 'Brute force substring search',
    problemStatement: `Find all occurrences of a pattern P in a text T. Return all starting indices where P appears. Simplest approach: try every alignment and check character-by-character.`,
    explanation: `For each possible alignment i from 0 to n-m, compare pattern[0..m-1] with text[i..i+m-1]. If all m chars match, record i. If any mismatch, move to the next alignment. Time complexity: O((n-m+1) * m).`,
    realLifeAnalogy: `Like looking for a specific word in a book by placing your finger under each starting position and comparing letters one by one.`,
    constraints: ['1 <= pattern.length <= text.length <= 100'],
    examples: [
      { input: 'ABC|ABCDABCDABC', target: null, output: '0, 4, 8' },
      { input: 'AABA|AABAACAADAABAAABAA', target: null, output: '0, 9, 13' },
      { input: 'xyz|abcdxyzabcxyz', target: null, output: '4, 11' }
    ],
    javaCode: `public class Solution {
    public List<Integer> naiveMatch(String text, String pattern) {
        List<Integer> result = new ArrayList<>();
        int n = text.length(), m = pattern.length();
        
        for (int i = 0; i <= n - m; i++) {
            int j = 0;
            while (j < m && text.charAt(i + j) == pattern.charAt(j)) {
                j++;
            }
            if (j == m) {
                result.add(i);
            }
        }
        
        return result;
    }
}`,
    visualizationType: 'string-match',
    requiresTarget: false,
    customStringInput: true,
    inputLabel: 'Pattern | Text',
    inputPlaceholder: 'e.g., ABC|ABCDABCDABC'
  },
  {
    id: 'kmp',
    title: 'KMP Algorithm',
    module: 'String Algorithms',
    difficulty: 'Medium',
    estimatedTime: '25 min',
    concept: 'Skip smartly using LPS (failure) array',
    problemStatement: `Find all occurrences of pattern P in text T in O(n + m) time. KMP uses a precomputed LPS array to avoid re-checking characters after a mismatch.`,
    explanation: `Precompute LPS (Longest Proper Prefix that is also a Suffix) for the pattern. On mismatch at position j, don't restart from i+1 — instead, use j = lps[j-1] to skip ahead intelligently, reusing partial match information.`,
    realLifeAnalogy: `Like memorizing common prefixes so you can jump ahead when partial matches fail, instead of re-reading the same characters.`,
    constraints: ['1 <= pattern.length <= text.length <= 100'],
    examples: [
      { input: 'ABABC|ABABDABABCABAB', target: null, output: '5' },
      { input: 'AABA|AABAACAADAABAAABAA', target: null, output: '0, 9, 13' },
      { input: 'ABAB|ABABABAB', target: null, output: '0, 2, 4' }
    ],
    javaCode: `public class Solution {
    public List<Integer> kmp(String text, String pattern) {
        List<Integer> result = new ArrayList<>();
        int[] lps = computeLPS(pattern);
        int i = 0, j = 0, n = text.length(), m = pattern.length();
        
        while (i < n) {
            if (text.charAt(i) == pattern.charAt(j)) {
                i++; j++;
                if (j == m) {
                    result.add(i - j);
                    j = lps[j - 1];  // Continue searching
                }
            } else if (j > 0) {
                j = lps[j - 1];  // Skip using LPS
            } else {
                i++;
            }
        }
        
        return result;
    }
}`,
    visualizationType: 'string-match',
    requiresTarget: false,
    customStringInput: true,
    inputLabel: 'Pattern | Text',
    inputPlaceholder: 'e.g., ABABC|ABABDABABCABAB'
  },
  {
    id: 'rabin-karp',
    title: 'Rabin-Karp Algorithm',
    module: 'String Algorithms',
    difficulty: 'Hard',
    estimatedTime: '25 min',
    concept: 'Rolling hash for fast pattern comparison',
    problemStatement: `Find all occurrences of pattern P in text T using hashing. Compute pattern's hash, then slide a window over text — comparing hashes first, and only verifying char-by-char on hash match.`,
    explanation: `Precompute pattern hash. For each window in the text, compute its hash. If hashes match, do a char-by-char verification (to handle hash collisions). Use a rolling hash to compute the next window's hash in O(1) instead of O(m).`,
    realLifeAnalogy: `Like comparing photos by their thumbnail signature first — only pull out and check the full photo when signatures match.`,
    constraints: ['1 <= pattern.length <= text.length <= 100'],
    examples: [
      { input: 'ABC|ABCDABCDABC', target: null, output: '0, 4, 8' },
      { input: 'GEEK|GEEKSFORGEEKS', target: null, output: '0, 8' },
      { input: 'xy|xyxyxy', target: null, output: '0, 2, 4' }
    ],
    javaCode: `public class Solution {
    static final int BASE = 31;
    static final int MOD = 101;
    
    public List<Integer> rabinKarp(String text, String pattern) {
        List<Integer> result = new ArrayList<>();
        int n = text.length(), m = pattern.length();
        
        long patternHash = 0, windowHash = 0;
        long h = 1;
        for (int i = 0; i < m - 1; i++) h = (h * BASE) % MOD;
        
        for (int i = 0; i < m; i++) {
            patternHash = (patternHash * BASE + pattern.charAt(i)) % MOD;
            windowHash  = (windowHash * BASE + text.charAt(i)) % MOD;
        }
        
        for (int i = 0; i <= n - m; i++) {
            if (windowHash == patternHash) {
                if (text.substring(i, i + m).equals(pattern)) {
                    result.add(i);
                }
            }
            if (i < n - m) {
                windowHash = ((windowHash - text.charAt(i) * h) * BASE + text.charAt(i + m)) % MOD;
                if (windowHash < 0) windowHash += MOD;
            }
        }
        
        return result;
    }
}`,
    visualizationType: 'string-match',
    requiresTarget: false,
    customStringInput: true,
    inputLabel: 'Pattern | Text',
    inputPlaceholder: 'e.g., GEEK|GEEKSFORGEEKS'
  }
];

export const getLessonById = (id) => {
  return lessonsData.find(lesson => lesson.id === id);
};
